"use client"

import { useEffect, useRef } from "react"

/**
 * ConfirmDialog
 * Props:
 *   isOpen      – boolean
 *   title       – string (optional, defaults to "Confirmer l'action")
 *   message     – string
 *   itemName    – string (optional) – shows a highlighted item name in the body
 *   confirmLabel – string (optional, defaults to "Confirmer")
 *   variant     – "danger" | "warning" | "info"  (default "danger")
 *   onConfirm   – () => void
 *   onCancel    – () => void
 *   isDarkMode  – boolean
 */
const ConfirmDialog = ({
  isOpen,
  title,
  message,
  itemName,
  confirmLabel = "Confirmer",
  variant = "danger",
  onConfirm,
  onCancel,
  isDarkMode,
}) => {
  const cancelRef = useRef(null)

  useEffect(() => {
    if (!isOpen) { document.body.style.overflow = ""; return }
    document.body.style.overflow = "hidden"
    const t = setTimeout(() => cancelRef.current?.focus(), 50)
    const onKey = (e) => { if (e.key === "Escape") onCancel?.() }
    window.addEventListener("keydown", onKey)
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey) }
  }, [isOpen, onCancel])

  useEffect(() => () => { document.body.style.overflow = "" }, [])

  if (!isOpen) return null

  const cfg = {
    danger:  { icon: "🗑️", iconBg: "#fef2f2", iconColor: "#ef4444", btnBg: "linear-gradient(135deg,#ef4444,#dc2626)", btnShadow: "rgba(239,68,68,0.35)" },
    warning: { icon: "⚠️", iconBg: "#fffbeb", iconColor: "#f59e0b", btnBg: "linear-gradient(135deg,#f59e0b,#d97706)", btnShadow: "rgba(245,158,11,0.35)" },
    info:    { icon: "ℹ️", iconBg: "#eff6ff", iconColor: "#3b82f6", btnBg: "linear-gradient(135deg,#3b82f6,#2563eb)", btnShadow: "rgba(59,130,246,0.35)" },
  }[variant]

  const dark = isDarkMode

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onCancel}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          zIndex: 9998,
          animation: "cdFadeIn 0.18s ease",
        }}
      />

      {/* Dialog */}
      <div
        role="alertdialog"
        aria-modal="true"
        style={{
          position: "fixed", top: "50%", left: "50%",
          transform: "translate(-50%,-50%)",
          zIndex: 9999,
          width: "min(420px,92vw)",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: dark
            ? "0 32px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.08)"
            : "0 32px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)",
          background: dark ? "#0f172a" : "#ffffff",
          animation: "cdSlideIn 0.22s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        {/* Top accent bar */}
        <div style={{
          height: "4px",
          background: variant === "danger"
            ? "linear-gradient(90deg,#ef4444,#f97316)"
            : variant === "warning"
            ? "linear-gradient(90deg,#f59e0b,#f97316)"
            : "linear-gradient(90deg,#3b82f6,#6366f1)",
        }} />

        {/* Body */}
        <div style={{ padding: "2rem 2rem 1.5rem", textAlign: "center" }}>
          {/* Icon circle */}
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: dark ? cfg.iconColor + "22" : cfg.iconBg,
            border: `2px solid ${cfg.iconColor}33`,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 1.25rem",
            fontSize: "1.8rem",
          }}>
            {cfg.icon}
          </div>

          {/* Title */}
          <h3 style={{
            margin: "0 0 0.5rem",
            fontSize: "1.15rem",
            fontWeight: 700,
            color: dark ? "#f1f5f9" : "#0f172a",
            fontFamily: "'Inter','Segoe UI',sans-serif",
          }}>
            {title || "Confirmer l'action"}
          </h3>

          {/* Message */}
          <p style={{
            margin: "0 0 (itemName ? '0.5rem' : '0')",
            color: dark ? "#94a3b8" : "#64748b",
            fontSize: "0.95rem",
            lineHeight: 1.6,
            fontFamily: "'Inter','Segoe UI',sans-serif",
          }}>
            {message}
          </p>

          {/* Item name highlight */}
          {itemName && (
            <p style={{
              margin: "0.5rem 0 0",
              padding: "0.5rem 1rem",
              borderRadius: "8px",
              background: dark ? "rgba(255,255,255,0.06)" : "#f8fafc",
              border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "#e2e8f0"}`,
              color: dark ? "#e2e8f0" : "#1e293b",
              fontWeight: 600,
              fontSize: "0.9rem",
              fontFamily: "'Inter','Segoe UI',sans-serif",
            }}>
              « {itemName} »
            </p>
          )}
        </div>

        {/* Divider */}
        <div style={{ height: "1px", background: dark ? "rgba(255,255,255,0.07)" : "#f1f5f9" }} />

        {/* Actions */}
        <div style={{
          display: "flex", gap: "0.75rem",
          padding: "1.25rem 2rem",
          justifyContent: "flex-end",
          background: dark ? "rgba(255,255,255,0.02)" : "#fafafa",
        }}>
          <button
            ref={cancelRef}
            onClick={onCancel}
            style={{
              padding: "0.65rem 1.4rem",
              borderRadius: "10px",
              border: `2px solid ${dark ? "rgba(255,255,255,0.12)" : "#e2e8f0"}`,
              background: "transparent",
              color: dark ? "#94a3b8" : "#64748b",
              fontWeight: 600, fontSize: "0.9rem",
              cursor: "pointer",
              fontFamily: "'Inter','Segoe UI',sans-serif",
              transition: "all 0.2s",
            }}
            onMouseEnter={e => { e.target.style.borderColor = "#ec4899"; e.target.style.color = "#ec4899" }}
            onMouseLeave={e => { e.target.style.borderColor = dark ? "rgba(255,255,255,0.12)" : "#e2e8f0"; e.target.style.color = dark ? "#94a3b8" : "#64748b" }}
          >
            Annuler
          </button>

          <button
            onClick={onConfirm}
            style={{
              padding: "0.65rem 1.6rem",
              borderRadius: "10px",
              border: "none",
              background: cfg.btnBg,
              color: "#fff",
              fontWeight: 700, fontSize: "0.9rem",
              cursor: "pointer",
              boxShadow: `0 4px 14px ${cfg.btnShadow}`,
              fontFamily: "'Inter','Segoe UI',sans-serif",
              transition: "all 0.2s",
            }}
            onMouseEnter={e => { e.target.style.transform = "translateY(-2px)"; e.target.style.boxShadow = `0 8px 24px ${cfg.btnShadow}` }}
            onMouseLeave={e => { e.target.style.transform = ""; e.target.style.boxShadow = `0 4px 14px ${cfg.btnShadow}` }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes cdFadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes cdSlideIn { from { opacity:0; transform:translate(-50%,-48%) scale(0.92) } to { opacity:1; transform:translate(-50%,-50%) scale(1) } }
      `}</style>
    </>
  )
}

export default ConfirmDialog
